/**
 * Subject-specific AI prompt generator for question bank CSV generation.
 * Returns precise, accurate prompts tailored to each subject.
 */

interface PromptConfig {
  subjectLabel: string;
  mode: 'starter' | 'worksheet' | 'quiz' | 'drill';
  /** Admin prompts include sub-topic and year level placeholders */
  isAdmin: boolean;
}

// ─── Subject-Specific Examples & Rules ─────────────────────────────────────

interface SubjectMeta {
  /** Example topic for this subject */
  topicExample: string;
  /** Example sub-topic */
  subTopicExample: string;
  /** Worksheet-specific example topic */
  wsTopicExample: string;
  /** Worksheet-specific example sub-topic */
  wsSubTopicExample: string;
  /** Quiz example topic */
  quizTopicExample: string;
  /** Quiz example sub-topic */
  quizSubTopicExample: string;
  /** Subject-specific formatting instructions */
  formattingRules: string;
  /** Subject-specific quality guidance */
  qualityNotes: string;
  /** Whether to mention Desmos/graphing */
  usesDesmos: boolean;
}

const SUBJECT_META: Record<string, SubjectMeta> = {
  mathematics: {
    topicExample: 'Algebra',
    subTopicExample: 'Expanding Brackets',
    wsTopicExample: 'Fractions',
    wsSubTopicExample: 'Adding Fractions',
    quizTopicExample: 'Geometry',
    quizSubTopicExample: 'Angles in Polygons',
    formattingRules: `Format all mathematical expressions using KaTeX: single $ for inline (e.g. $3x+5$), double $$ for display mode (e.g. $$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$).
Use proper mathematical notation: fractions (\\frac{}{}), square roots (\\sqrt{}), powers (^), subscripts (_), etc.
For graphing questions, set UsesDesmos to TRUE and put the Desmos-compatible expression in 'GraphExpression' (e.g. y=2x+5). Otherwise set UsesDesmos to FALSE and leave GraphExpression empty.`,
    qualityNotes: `Ensure numerical answers are exact (not rounded). Include a mix of: calculation, problem-solving, reasoning, and application questions.
Use proper units where applicable (cm, m, kg, etc.). Avoid ambiguous phrasing — every question must have exactly one correct answer.`,
    usesDesmos: true,
  },

  science: {
    topicExample: 'Forces and Motion',
    subTopicExample: 'Newton\'s Laws',
    wsTopicExample: 'Chemical Reactions',
    wsSubTopicExample: 'Balancing Equations',
    quizTopicExample: 'Cells and Organisation',
    quizSubTopicExample: 'Cell Structure',
    formattingRules: `Use KaTeX for scientific formulae and equations: single $ for inline (e.g. $F = ma$, $E = mc^2$), double $$ for display mode.
For chemical equations use appropriate notation (e.g. $2H_2 + O_2 \\rightarrow 2H_2O$).
Use proper SI units in all answers and explanations (N, J, m/s, mol, etc.).
Set UsesDesmos to FALSE and leave GraphExpression empty unless the question explicitly involves graph interpretation.`,
    qualityNotes: `Include a balance of recall, application, and analysis questions. Ensure all scientific facts are accurate and up-to-date.
For Physics: include calculation questions with proper formula application. For Chemistry: include equation balancing, reaction types, and structure questions. For Biology: include labelling, process description, and comparison questions.
State units explicitly in answers. Include key scientific vocabulary in explanations.`,
    usesDesmos: false,
  },

  physics: {
    topicExample: 'Electricity',
    subTopicExample: 'Series and Parallel Circuits',
    wsTopicExample: 'Energy',
    wsSubTopicExample: 'Energy Transfers',
    quizTopicExample: 'Waves',
    quizSubTopicExample: 'Wave Properties',
    formattingRules: `Use KaTeX for all physics formulae: single $ for inline (e.g. $V = IR$, $s = vt$), double $$ for display mode (e.g. $$P = \\frac{E}{t}$$).
Always include correct SI units in formulae and answers (N, J, W, V, A, Ω, m/s, m/s², kg, etc.).
Set UsesDesmos to TRUE for graph-based questions (e.g. velocity-time graphs, V-I characteristics). Otherwise set UsesDesmos to FALSE and leave GraphExpression empty.`,
    qualityNotes: `Include a mix of: recall of formulae, substitution and calculation, graph interpretation, and practical application questions.
Ensure all constants used are standard (e.g. $g = 9.8\\,\\text{m/s}^2$ or $10\\,\\text{m/s}^2$ as appropriate for GCSE).
State what is given, what is asked, and show clear working in explanations. Every numerical answer must include the correct unit.`,
    usesDesmos: true,
  },

  chemistry: {
    topicExample: 'Atomic Structure',
    subTopicExample: 'Electronic Configuration',
    wsTopicExample: 'Acids and Alkalis',
    wsSubTopicExample: 'pH Scale',
    quizTopicExample: 'The Periodic Table',
    quizSubTopicExample: 'Group 1 Elements',
    formattingRules: `Use KaTeX for chemical formulae and equations: single $ for inline (e.g. $NaCl$, $H_2SO_4$), double $$ for display equations.
Use subscripts for chemical formulae (e.g. $H_2O$, $CO_2$). Use \\rightarrow for reaction arrows, \\rightleftharpoons for reversible reactions.
State symbols should be included where appropriate: (s), (l), (g), (aq).
Set UsesDesmos to FALSE and leave GraphExpression empty.`,
    qualityNotes: `Ensure all chemical equations are correctly balanced. Include a mix of: recall, equation writing, calculation (moles, mass, concentration), and explanation questions.
Use correct IUPAC nomenclature. Ensure all relative atomic masses used are consistent with the periodic table.
For calculation questions, show clear step-by-step working including formula, substitution, and final answer with units.`,
    usesDesmos: false,
  },

  biology: {
    topicExample: 'Ecology',
    subTopicExample: 'Food Chains and Webs',
    wsTopicExample: 'Inheritance',
    wsSubTopicExample: 'Punnett Squares',
    quizTopicExample: 'Organisation',
    quizSubTopicExample: 'The Digestive System',
    formattingRules: `Use KaTeX for any biological formulae or equations: single $ for inline (e.g. $6CO_2 + 6H_2O \\rightarrow C_6H_{12}O_6 + 6O_2$).
Use proper scientific terminology and italicise species names where relevant (e.g. *Homo sapiens*).
Set UsesDesmos to FALSE and leave GraphExpression empty.`,
    qualityNotes: `Include a mix of: recall, application, data interpretation, and extended response questions.
Ensure all biological processes are described accurately (e.g. mitosis stages, enzyme function, photosynthesis steps).
Use correct key terminology in both questions and answers. Include diagrams descriptions where relevant (e.g. "Label the parts of the heart").
For genetics questions, use correct notation (dominant/recessive alleles, genotype/phenotype).`,
    usesDesmos: false,
  },

  english: {
    topicExample: 'Reading Comprehension',
    subTopicExample: 'Inference and Deduction',
    wsTopicExample: 'Grammar and Punctuation',
    wsSubTopicExample: 'Semicolons and Colons',
    quizTopicExample: 'Writing Skills',
    quizSubTopicExample: 'Persuasive Techniques',
    formattingRules: `Do NOT use KaTeX or mathematical formatting. Write all questions and answers in plain text.
Use quotation marks for literary quotes and extracts. Use italics (*text*) for titles of texts, poems, and plays.
Set UsesDesmos to FALSE and leave GraphExpression empty.`,
    qualityNotes: `Include a mix of: comprehension, grammar, vocabulary, literary techniques, and analytical questions.
For grammar questions, provide clear example sentences. For comprehension, include short text extracts (2-3 sentences) within the question.
Ensure spelling and grammar in the questions themselves is impeccable. Use age-appropriate vocabulary.
For literature questions, reference widely-known texts or provide original extracts rather than assuming specific set texts.`,
    usesDesmos: false,
  },

  history: {
    topicExample: 'World War I',
    subTopicExample: 'Causes of World War I',
    wsTopicExample: 'Medieval England',
    wsSubTopicExample: 'The Feudal System',
    quizTopicExample: 'The Cold War',
    quizSubTopicExample: 'The Berlin Wall',
    formattingRules: `Do NOT use KaTeX or mathematical formatting. Write all questions and answers in plain text.
Include specific dates, names, and events where relevant. Use quotation marks for historical quotes.
Set UsesDesmos to FALSE and leave GraphExpression empty.`,
    qualityNotes: `Include a mix of: factual recall, source analysis, cause-and-consequence, significance, and comparison questions.
Ensure all historical facts, dates, and attributions are accurate. Avoid presentism — frame questions within historical context.
For source-based questions, provide brief extracts or descriptions. Answers should demonstrate understanding of multiple perspectives where appropriate.`,
    usesDesmos: false,
  },

  geography: {
    topicExample: 'Weather and Climate',
    subTopicExample: 'The Water Cycle',
    wsTopicExample: 'Tectonic Hazards',
    wsSubTopicExample: 'Earthquakes',
    quizTopicExample: 'Urban Issues',
    quizSubTopicExample: 'Urbanisation',
    formattingRules: `Use KaTeX sparingly for any geographical data or statistics: single $ for inline (e.g. $2.5\\,\\text{mm}$ rainfall).
Include map references, compass directions, and grid references where relevant.
Set UsesDesmos to FALSE and leave GraphExpression empty.`,
    qualityNotes: `Include a mix of: knowledge recall, data interpretation, case study application, and evaluation questions.
Ensure all geographical facts (population data, locations, statistics) are accurate. Use real-world examples and case studies.
For fieldwork-related questions, reference standard techniques (e.g. sampling methods, data presentation).
Include proper geographical terminology in both questions and answers.`,
    usesDesmos: false,
  },

  'computer science': {
    topicExample: 'Programming Concepts',
    subTopicExample: 'Selection Statements',
    wsTopicExample: 'Data Representation',
    wsSubTopicExample: 'Binary Conversion',
    quizTopicExample: 'Computer Networks',
    quizSubTopicExample: 'Network Topologies',
    formattingRules: `Use code formatting for programming examples by wrapping code in backticks. Use KaTeX for binary/hex: single $ for inline (e.g. $1010_2 = 10_{10}$).
For algorithm questions, describe steps clearly and use pseudocode where appropriate.
Set UsesDesmos to FALSE and leave GraphExpression empty.`,
    qualityNotes: `Include a mix of: programming logic, binary/hex conversion, networking concepts, data structures, and Boolean logic questions.
Ensure all code examples are syntactically correct. Use pseudocode that is language-agnostic where possible.
For binary/hex calculations, show clear conversion steps in explanations. For programming, include trace tables in explanations where helpful.
Cover both theoretical concepts and practical application.`,
    usesDesmos: false,
  },

  psychology: {
    topicExample: 'Memory',
    subTopicExample: 'The Multi-Store Model',
    wsTopicExample: 'Social Influence',
    wsSubTopicExample: 'Conformity',
    quizTopicExample: 'Approaches in Psychology',
    quizSubTopicExample: 'The Behaviourist Approach',
    formattingRules: `Do NOT use KaTeX or mathematical formatting unless discussing statistics (e.g. $p < 0.05$).
Use proper APA-style referencing format for study citations (e.g. Milgram, 1963).
Set UsesDesmos to FALSE and leave GraphExpression empty.`,
    qualityNotes: `Include a mix of: knowledge recall, study description, evaluation (strengths/limitations), and application questions.
Reference specific studies accurately (researcher name, year, method, findings, conclusions).
Ensure all psychological terminology is used correctly (e.g. operant conditioning, schemas, demand characteristics).
Include methodological questions (e.g. identify the IV/DV, suggest improvements, discuss ethics).`,
    usesDesmos: false,
  },
};

// ─── Fallback for unknown subjects ─────────────────────────────────────────
const DEFAULT_META: SubjectMeta = {
  topicExample: 'Core Topic',
  subTopicExample: 'Key Concept',
  wsTopicExample: 'Foundation Topic',
  wsSubTopicExample: 'Essential Skills',
  quizTopicExample: 'Main Topic',
  quizSubTopicExample: 'Key Area',
  formattingRules: `Use KaTeX for any mathematical or scientific notation: single $ for inline, double $$ for display mode.
For non-mathematical subjects, write questions and answers in plain text.
Set UsesDesmos to FALSE and leave GraphExpression empty unless specifically relevant.`,
  qualityNotes: `Ensure all facts and information are accurate. Include a variety of question types (recall, application, analysis, evaluation).
Use age-appropriate language. Every question must have exactly one unambiguous correct answer.`,
  usesDesmos: false,
};

// ─── Helper to match subject label to meta ─────────────────────────────────

function getSubjectMeta(subjectLabel: string): SubjectMeta {
  const normalised = subjectLabel.trim().toLowerCase();

  // Exact match first
  if (SUBJECT_META[normalised]) return SUBJECT_META[normalised];

  // Partial match (e.g. "Mathematics" matches "mathematics", "Maths" matches "mathematics")
  if (normalised.includes('math')) return SUBJECT_META.mathematics;
  if (normalised.includes('physic')) return SUBJECT_META.physics;
  if (normalised.includes('chem')) return SUBJECT_META.chemistry;
  if (normalised.includes('bio')) return SUBJECT_META.biology;
  if (normalised === 'science' || normalised.includes('combined science')) return SUBJECT_META.science;
  if (normalised.includes('english') || normalised.includes('literacy')) return SUBJECT_META.english;
  if (normalised.includes('histor')) return SUBJECT_META.history;
  if (normalised.includes('geograph')) return SUBJECT_META.geography;
  if (normalised.includes('computer') || normalised.includes('computing') || normalised.includes('ict')) return SUBJECT_META['computer science'];
  if (normalised.includes('psych')) return SUBJECT_META.psychology;

  return DEFAULT_META;
}

// ─── Main Prompt Generator ─────────────────────────────────────────────────

export function getAIPrompt(subjectLabel: string, mode: string, isAdmin: boolean): string {
  const meta = getSubjectMeta(subjectLabel);
  const subject = subjectLabel || 'the subject';

  const desmosLine = meta.usesDesmos
    ? `For graphing questions, set UsesDesmos to TRUE and put the Desmos-compatible expression in 'GraphExpression' (e.g. y=2x+5). Otherwise set UsesDesmos to FALSE and leave GraphExpression empty.`
    : `Set UsesDesmos to FALSE and leave GraphExpression empty.`;

  if (mode === 'starter') {
    const topicEg = meta.topicExample;
    const subTopicEg = meta.subTopicExample;

    if (isAdmin) {
      return `Generate [ENTER NUMBER, e.g. 20] high-quality ${subject} starter questions for the topic '${topicEg}' and sub-topic '${subTopicEg}' suitable for Year [ENTER YEAR LEVEL, e.g. 9]. Output strictly as a CSV.

Required columns:
Topic,Sub Topic,Year Level,Question,Answer,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty

CRITICAL INSTRUCTIONS:
The 'Topic' column MUST be exactly the topic you are generating for (e.g. ${topicEg}).
The 'Sub Topic' column MUST be exactly the sub-topic you are generating for (e.g. ${subTopicEg}).
The 'Year Level' column MUST be exactly the year level specified (e.g. 9).
The 'Question' column must contain only the question itself. Do not include multiple-choice options.
The 'Answer' column must contain the complete correct answer only.
The 'Explanation' column must give a clear, step-by-step solution.
${meta.formattingRules}
${desmosLine}
'ImageUrl' is optional — leave empty unless you have an image URL to include.
Difficulty must be a single integer from 4 to 9 with a sensible spread.
Make questions accurate, varied, and appropriate for starter tasks.
Do not output markdown fences or commentary. Output raw CSV only.

QUALITY CONTROL RULES:
Every row must have exactly these 10 columns:
Topic,Sub Topic,Year Level,Question,Answer,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty
Do not leave required fields blank. GraphExpression and ImageUrl may be empty.
${meta.qualityNotes}

Optional topic-specific guidance:
[PASTE TOPIC-SPECIFIC NOTES HERE IF NEEDED]`;
    } else {
      return `Generate [ENTER NUMBER, e.g. 20] high-quality ${subject} starter questions for the topic '[ENTER TOPIC, e.g. ${topicEg}]'. Output strictly as a CSV.

Required columns:
Topic,Sub Topic,Year Level,Question,Answer,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty

CRITICAL INSTRUCTIONS:
The 'Topic' column MUST be exactly the topic provided above (e.g. ${topicEg}).
The 'Sub Topic' column should be assigned by you based on the specific sub-topic of the question (e.g. ${subTopicEg}). Reuse the same sub-topic name for questions covering the same concept.
Assign a valid 'Year Level' for each row using one of: 7, 8, 9, 10, 11.
The 'Question' column must contain only the question itself. Do not include multiple-choice options.
The 'Answer' column must contain the complete correct answer only.
The 'Explanation' column must give a clear, step-by-step solution.
${meta.formattingRules}
${desmosLine}
'ImageUrl' is optional — leave empty unless you have an image URL.
Difficulty must be a single integer from 4 to 9 with a sensible spread.
Make questions accurate, varied, and appropriate for starter tasks.
Do not output markdown fences or commentary. Output raw CSV only.

QUALITY CONTROL RULES:
Every row must have exactly these 10 columns:
Topic,Sub Topic,Year Level,Question,Answer,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty
Do not leave required fields blank. GraphExpression and ImageUrl may be empty.
${meta.qualityNotes}

Optional topic-specific guidance:
[PASTE TOPIC-SPECIFIC NOTES HERE IF NEEDED]`;
    }
  }

  if (mode === 'worksheet') {
    const topicEg = meta.wsTopicExample;
    const subTopicEg = meta.wsSubTopicExample;

    if (isAdmin) {
      return `Generate [ENTER NUMBER, e.g. 20] high-quality ${subject} worksheet questions for the topic '${topicEg}' and sub-topic '${subTopicEg}' suitable for Year [ENTER YEAR LEVEL, e.g. 8]. Output strictly as a CSV.

Required columns:
Topic,Sub Topic,Year Level,Question,Answer,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty

CRITICAL INSTRUCTIONS:
The 'Topic' column MUST be exactly the topic you are generating for (e.g. ${topicEg}).
The 'Sub Topic' column MUST be exactly the sub-topic you are generating for (e.g. ${subTopicEg}).
The 'Year Level' column MUST be exactly the year level specified (e.g. 8).
Questions can be a mix of multiple choice and written answers. For multiple-choice, include options within the 'Question' column text.
The 'Answer' column must contain the complete correct answer only.
The 'Explanation' column must give a clear, step-by-step solution.
${meta.formattingRules}
${desmosLine}
'ImageUrl' is optional — leave empty unless you have an image URL.
Difficulty must be a single integer from 4 to 9 with a sensible spread.
Make questions accurate, varied, progressing in difficulty.
Do not output markdown fences or commentary. Output raw CSV only.

QUALITY CONTROL RULES:
Every row must have exactly these 10 columns:
Topic,Sub Topic,Year Level,Question,Answer,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty
Do not leave required fields blank. GraphExpression and ImageUrl may be empty.
${meta.qualityNotes}

Optional topic-specific guidance:
[PASTE TOPIC-SPECIFIC NOTES HERE IF NEEDED]`;
    } else {
      return `Generate [ENTER NUMBER, e.g. 20] high-quality ${subject} worksheet questions for the topic '[ENTER TOPIC, e.g. ${topicEg}]'. Output strictly as a CSV.

Required columns:
Topic,Sub Topic,Year Level,Question,Answer,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty

CRITICAL INSTRUCTIONS:
The 'Topic' column MUST be exactly the topic provided above (e.g. ${topicEg}).
The 'Sub Topic' column should be assigned by you based on the specific sub-topic of the question (e.g. ${subTopicEg}). Reuse the same sub-topic name for questions covering the same concept.
Assign a valid 'Year Level' for each row using one of: 7, 8, 9, 10, 11.
Questions can be a mix of multiple choice and written answers. For multiple-choice, include options within the 'Question' column text.
The 'Answer' column must contain the complete correct answer only.
The 'Explanation' column must give a clear, step-by-step solution.
${meta.formattingRules}
${desmosLine}
'ImageUrl' is optional — leave empty unless you have an image URL.
Difficulty must be a single integer from 4 to 9 with a sensible spread.
Make questions accurate, varied, progressing in difficulty.
Do not output markdown fences or commentary. Output raw CSV only.

QUALITY CONTROL RULES:
Every row must have exactly these 10 columns:
Topic,Sub Topic,Year Level,Question,Answer,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty
Do not leave required fields blank. GraphExpression and ImageUrl may be empty.
${meta.qualityNotes}

Optional topic-specific guidance:
[PASTE TOPIC-SPECIFIC NOTES HERE IF NEEDED]`;
    }
  }

  // Quiz / Drill mode (multiple choice)
  const topicEg = meta.quizTopicExample;
  const subTopicEg = meta.quizSubTopicExample;

  if (isAdmin) {
    return `Generate [ENTER NUMBER, e.g. 30] high-quality ${subject} multiple-choice questions for the topic '${topicEg}' and sub-topic '${subTopicEg}' suitable for Year [ENTER YEAR LEVEL, e.g. 10] ${mode === 'drill' ? 'rapid practice drills' : 'assessments'}. Output strictly as a CSV.

Required columns: Topic,Sub Topic,Year Level,Question,Option1,Option2,Option3,Option4,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty

CRITICAL INSTRUCTIONS FOR MULTIPLE CHOICE:
1. The 'Topic' column MUST be exactly the topic you are generating for (e.g. ${topicEg}).
2. The 'Sub Topic' column MUST be exactly the sub-topic you are generating for (e.g. ${subTopicEg}).
3. The 'Year Level' column MUST be exactly the year level specified (e.g. 10).
4. Option1 MUST ALWAYS be the correct answer. The system will shuffle them automatically for the student.
5. Option2, Option3, and Option4 MUST be highly plausible distractors based on common student misconceptions or calculation errors. Do NOT use obviously wrong answers.
6. ${meta.formattingRules}
7. ${desmosLine}
8. 'ImageUrl' is optional — leave empty unless you have an image URL.
9. Explanations must provide a clear, step-by-step solution, and briefly explain why the correct answer is right and the common pitfall that leads to the wrong options.
10. Difficulty must be an integer (4-9), scaling properly. Grade 4 is foundation/recall, Grade 7 is higher/application, Grade 9 is complex/synthesis.
11. Do NOT output markdown blocks, just raw comma-separated text.

QUALITY CONTROL:
${meta.qualityNotes}
Ensure questions are concise and perfectly suited for a timed multiple-choice format.`;
  } else {
    return `Generate [ENTER NUMBER, e.g. 30] high-quality ${subject} multiple-choice questions for the topic '[ENTER TOPIC, e.g. ${topicEg}]' suitable for ${mode === 'drill' ? 'rapid practice drills' : 'assessments'}. Output strictly as a CSV.

Required columns: Topic,Sub Topic,Year Level,Question,Option1,Option2,Option3,Option4,Explanation,UsesDesmos,GraphExpression,ImageUrl,Difficulty

CRITICAL INSTRUCTIONS FOR MULTIPLE CHOICE:
1. The 'Topic' column MUST be exactly the topic provided above (e.g. ${topicEg}).
2. The 'Sub Topic' column should be assigned by you based on the specific sub-topic (e.g. ${subTopicEg}). Reuse the same sub-topic name for questions covering the same concept.
3. Assign a valid 'Year Level' for each row using one of: 7, 8, 9, 10, 11.
4. Option1 MUST ALWAYS be the correct answer. The system will shuffle them automatically for the student.
5. Option2, Option3, and Option4 MUST be highly plausible distractors based on common student misconceptions or calculation errors. Do NOT use obviously wrong answers.
6. ${meta.formattingRules}
7. ${desmosLine}
8. 'ImageUrl' is optional — leave empty unless you have an image URL.
9. Explanations must provide a clear, step-by-step solution, and briefly explain why the correct answer is right and the common pitfall that leads to the wrong options.
10. Difficulty must be an integer (4-9), scaling properly. Grade 4 is foundation/recall, Grade 7 is higher/application, Grade 9 is complex/synthesis.
11. Do NOT output markdown blocks, just raw comma-separated text.

QUALITY CONTROL:
${meta.qualityNotes}
Ensure questions are concise and perfectly suited for a timed multiple-choice format.`;
  }
}
